/**
 * Table-settings presentation overrides.
 *
 * Two independent override sources merge over the static settings, in order:
 *
 * 1. Coordinate system (`coordinate_system_configs.json`): cartesian scenarios
 *    relabel the coordinate columns to "X"/"Y" and swap their validators to
 *    plain floats. Keyed by coordinate system rather than model type because
 *    the coordinate system is a property of the data.
 *
 * 2. Table labels (`generalSettings.tableLabels` + `table_label_presets.json`):
 *    a per-scenario rename of the vehicles / vehicle-types PAIR — one preset
 *    (Drivers, Personnel, Crews, …) or custom strings drives the table titles,
 *    the add/edit dialog titles, and the vehicle columns' headers, so an
 *    example about home-health can say "Personnel" and "Roles" without touching
 *    data. The label pack travels with the scenario (saved wholesale via
 *    /save_instance; fillMissing backfills scenarios that predate it).
 *
 * Only presentation keys may be overridden (titles, headers, validation,
 * adornments) — never `columnName`/`dataType`/`defaultValue` — so row shape,
 * uploads, solve payloads, and CSV round-trips stay identical either way.
 *
 * Deliberately store-free (mirrors `tripPairs.js`) so it stays unit-testable:
 * importing the selectors module pulls in the whole store, which does not
 * load under jest. Components should reach this through the hooks in
 * `store/functions/tableLabels.js`, which supply both store inputs.
 */

const STATIC_TABLE_SETTINGS = require('store/data/table_settings_static.json');
const COORDINATE_SYSTEM_CONFIGS = require('store/data/coordinate_system_configs.json');
const TABLE_LABEL_PRESETS = require('store/data/table_label_presets.json');

/** Presentation keys a coordinate system is allowed to override. Anything
 *  else in a tableOverrides block is ignored (protects row shape). */
const OVERRIDABLE_KEYS = ['columnHeader', 'validation', 'inputAdornment'];

/** The default naming. Also the fallback for blank custom fields, so a
 *  half-filled custom pack degrades to the standard words, never to "". */
const DEFAULT_LABEL_PACK = TABLE_LABEL_PRESETS.vehicles;

const cache = new Map();
const customPackCache = new Map();

/**
 * Resolve `generalSettings.tableLabels` to a concrete label pack.
 *
 * Referentially stable: presets return the module constant; custom packs are
 * cached by their string values, so equal inputs give the identical object —
 * safe in React deps and as a selector result.
 *
 * @param {Object|undefined} tableLabels - `generalSettings.tableLabels` (may be
 *   absent on states that predate the setting).
 * @returns {{menuLabel: string, vehicleSingular: string, vehiclePlural: string,
 *   typeSingular: string, typePlural: string, routesTitle: string}}
 */
export function resolveTableLabels(tableLabels) {
  const preset = tableLabels?.vehiclesPreset || 'vehicles';
  if (preset !== 'custom') {
    return TABLE_LABEL_PRESETS[preset] || DEFAULT_LABEL_PACK;
  }

  const custom = tableLabels?.custom || {};
  const clean = (value, fallback) =>
    (typeof value === 'string' && value.trim()) ? value.trim() : fallback;

  const vehicleSingular = clean(custom.vehicleSingular, DEFAULT_LABEL_PACK.vehicleSingular);
  const vehiclePlural = clean(custom.vehiclePlural, DEFAULT_LABEL_PACK.vehiclePlural);
  const typeSingular = clean(custom.typeSingular, DEFAULT_LABEL_PACK.typeSingular);
  const typePlural = clean(custom.typePlural, DEFAULT_LABEL_PACK.typePlural);

  const key = `${vehicleSingular}|${vehiclePlural}|${typeSingular}|${typePlural}`;
  const cached = customPackCache.get(key);
  if (cached) return cached;

  const pack = {
    menuLabel: 'Custom',
    vehicleSingular,
    vehiclePlural,
    typeSingular,
    typePlural,
    routesTitle: `${vehicleSingular} Routes`,
  };
  customPackCache.set(key, pack);
  return pack;
}

/** The presentation strings a label pack rewrites, per table. Anything not
 *  named here is untouched by labels. Returns null for the default pack so the
 *  standard naming keeps the zero-cost return-the-static-object path. */
function labelOverridesFor(tableName, pack) {
  if (!pack || pack === DEFAULT_LABEL_PACK) return null;

  if (tableName === 'vehicles') {
    return {
      title: pack.vehiclePlural,
      addRowTitle: `Add ${pack.vehicleSingular}`,
      editRowTitle: `Edit ${pack.vehicleSingular}`,
      columnHeader: {
        vehicleId: `${pack.vehicleSingular} ID`,
        vehicleName: `${pack.vehicleSingular} Name`,
        vehicleType: pack.typeSingular,
      },
    };
  }

  if (tableName === 'vehicle_types') {
    return {
      title: pack.typePlural,
      addRowTitle: `Add ${pack.typeSingular}`,
      editRowTitle: `Edit ${pack.typeSingular}`,
      columnHeader: {
        vehicleType: pack.typeSingular,
      },
    };
  }

  return null;
}

/**
 * Resolve the effective settings for a table: the static table settings with
 * the coordinate system's overrides merged over the overridable keys, then the
 * scenario's label overrides on top. Column-level merge — an override only
 * replaces the columns it names, other columns keep their static values. A
 * `null` coordinate override value is meaningful (e.g.
 * `inputAdornment.longitude: null` removes the degree adornment).
 *
 * Memoized per (tableName, coordinateSystem, label strings) — the cache key
 * uses the pack's VALUES, not object identity, because `tableLabels` comes
 * from mutable store state. The merged object is therefore referentially
 * stable for equal inputs — safe in React deps and effects.
 *
 * @param {string} tableName - Key into table_settings_static.json.
 * @param {'geographic'|'cartesian'} [coordinateSystem] - The scenario's coordinate system.
 * @param {Object} [tableLabels] - `generalSettings.tableLabels`; omit where labels
 *   cannot matter (e.g. validation-only callers).
 * @returns {Object} Effective table settings (the static object itself when
 *   nothing overrides this table).
 */
export function getEffectiveTableSettings(tableName, coordinateSystem, tableLabels) {
  const base = STATIC_TABLE_SETTINGS[tableName];
  if (!base) return base;

  const coordOverrides = COORDINATE_SYSTEM_CONFIGS?.[coordinateSystem]?.tableOverrides?.[tableName];
  const pack = resolveTableLabels(tableLabels);
  const labelOverrides = labelOverridesFor(tableName, pack);
  if (!coordOverrides && !labelOverrides) return base;

  const labelKey = labelOverrides
    ? `${pack.vehicleSingular}|${pack.vehiclePlural}|${pack.typeSingular}|${pack.typePlural}`
    : '';
  const cacheKey = `${tableName}|${coordinateSystem}|${labelKey}`;
  const cached = cache.get(cacheKey);
  if (cached) return cached;

  const merged = { ...base };
  if (coordOverrides) {
    for (const key of OVERRIDABLE_KEYS) {
      if (coordOverrides[key] && typeof coordOverrides[key] === 'object') {
        merged[key] = { ...(base[key] || {}), ...coordOverrides[key] };
      }
    }
  }
  if (labelOverrides) {
    merged.title = labelOverrides.title;
    merged.addRowTitle = labelOverrides.addRowTitle;
    merged.editRowTitle = labelOverrides.editRowTitle;
    merged.columnHeader = { ...(merged.columnHeader || {}), ...labelOverrides.columnHeader };
  }

  cache.set(cacheKey, merged);
  return merged;
}
